import React from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SUPPORTED_LANGUAGES } from "@/i18n";

/**
 * Language switcher with flag buttons (NL · FR · EN).
 *
 * Usage:
 *   <LanguageSwitcher variant="pill" />   // landing top-right
 *   <LanguageSwitcher variant="compact" /> // navbar inline
 *
 * - Updates i18next immediately (UI re-renders).
 * - Persists to localStorage automatically (via i18next-browser-languagedetector).
 * - For logged-in users, also persists `preferred_language` to the backend
 *   so the choice carries across devices.
 */
export default function LanguageSwitcher({ variant = "pill" }) {
    const { i18n } = useTranslation();
    const { user, setUser } = useAuth();
    const current = (i18n.resolvedLanguage || i18n.language || "nl").slice(0, 2);

    const change = async (code) => {
        if (code === current) return;
        await i18n.changeLanguage(code);
        if (user && code !== user.preferred_language) {
            try {
                await api.put("/users/me/language", { preferred_language: code });
                setUser?.({ ...user, preferred_language: code });
            } catch {
                /* non-fatal — i18n still updates locally */
            }
        }
    };

    const sizes = variant === "compact"
        ? "h-7 px-1.5 sm:px-2 text-[10px]"
        : "h-8 px-2 sm:px-2.5 text-[11px]";

    return (
        <div
            className="inline-flex items-center gap-0.5 rounded-sm bg-black/40 backdrop-blur-md border border-white/10"
            data-testid="language-switcher"
            role="group"
            aria-label="Language"
        >
            {SUPPORTED_LANGUAGES.map((lng) => {
                const active = current === lng.code;
                return (
                    <button
                        key={lng.code}
                        type="button"
                        onClick={() => change(lng.code)}
                        aria-pressed={active}
                        data-testid={`lang-btn-${lng.code}`}
                        className={`inline-flex items-center gap-1.5 ${sizes} uppercase tracking-wider font-bold transition rounded-sm ${
                            active
                                ? "bg-[#E5A93D] text-black"
                                : "text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100"
                        }`}
                    >
                        <span aria-hidden="true">{lng.flag}</span>
                        {/* Hide the textual short-code on phones — flag alone keeps the
                            switcher narrow enough to leave room for the logo + auth buttons. */}
                        <span className="hidden sm:inline">{lng.short}</span>
                    </button>
                );
            })}
        </div>
    );
}
